import { useRef } from 'react';
import { isEqual, keyBy, mapValues } from 'lodash-es';

import { createColumnVariableKeys } from './utils';

import type { RowData, Table } from '@tanstack/react-table';
import type {
  DataTableCellContent,
  DataTableColumnVirtualizationState,
  DataTableHeaderContent,
  DataTableHeaderGroupContent,
  DataTableRowContent,
  SortState,
} from './types';

export interface UseDataTableAdapterProps<TData extends RowData> {
  hasExpandColumn: boolean;
  hasSelectionColumn: boolean;
  sorting: SortState[];
  table: Table<TData>;
  columnVirtualization?: DataTableColumnVirtualizationState;
  isColumnVirtualizationEnabled?: boolean;
}

export default function useDataTableAdapter<TData extends RowData>(
  props: UseDataTableAdapterProps<TData>,
) {
  const {
    table,
    sorting,
    hasExpandColumn,
    hasSelectionColumn,
    columnVirtualization,
    isColumnVirtualizationEnabled = false,
  } = props;
  const renderedCenterColumnIds = columnVirtualization?.renderedCenterColumnIds;

  const result: {
    headerCount: number;
    headerGroups: DataTableHeaderGroupContent<TData>[];
    rows: DataTableRowContent<TData>[];
  } = {
    headerCount: 0,
    headerGroups: [],
    rows: [],
  };

  // headerGroups
  {
    const memoizedHeaderGroups = useRef<DataTableHeaderGroupContent<TData>[]>([]);
    const comparingHeaderGroups: DataTableHeaderGroupContent<TData>[] = [];
    const mHeaderGroupById = useRef<Record<string, DataTableHeaderGroupContent<TData>>>({});
    const mHeaderById = useRef<Record<string, DataTableHeaderContent<TData>>>({});
    const renderedHeaderIds = new Set<string>();

    const sortingMap = mapValues(
      keyBy(sorting, s => s.key),
      s => s.direction,
    );

    const headerGroups = table.getHeaderGroups();
    for (const headerGroup of headerGroups) {
      const visibleHeaders = renderedCenterColumnIds
        ? headerGroup.headers.filter(
            header =>
              header.column.getIsPinned() !== false ||
              renderedCenterColumnIds.has(header.column.id),
          )
        : headerGroup.headers;

      for (const header of visibleHeaders) {
        renderedHeaderIds.add(header.id);
        const prevHeader = mHeaderById.current[header.id];
        const nextHeader: DataTableHeaderContent<TData> = {
          id: header.id,
          CellTemplate: header.column.columnDef.header,
          cellTemplateProps: header.getContext(),
          colSpan: header.colSpan,
          isPlaceholder: header.isPlaceholder,
          maxSize: header.column.columnDef.maxSize,
          meta: header.column.columnDef.meta,
          minSize: header.column.columnDef.minSize,
          offset: `var(${createColumnVariableKeys(header.column.id).offset})`,
          resizable: header.column.getCanResize(),
          sortable: header.column.getCanSort(),
          sortDirection: sortingMap[header.id] ?? false,
          stickyPosition: header.column.getIsPinned(),
          width: `var(${createColumnVariableKeys(header.column.id).size})`,
        };

        const hasDiff =
          !prevHeader ||
          prevHeader.CellTemplate !== nextHeader.CellTemplate ||
          !isEqual(
            prevHeader.cellTemplateProps,
            nextHeader.cellTemplateProps,
          ) /* NOTE (@walter-24) `cellTemplateProps (header.getContext())`는 메모되어 있지 않은 객체임 (https://github.com/TanStack/table/blob/db745afdb867511ddd04a79150f89d3894a03532/packages/table-core/src/core/headers.ts#L251) */ ||
          prevHeader.colSpan !== nextHeader.colSpan ||
          prevHeader.isPlaceholder !== nextHeader.isPlaceholder ||
          prevHeader.maxSize !== nextHeader.maxSize ||
          prevHeader.meta !== nextHeader.meta ||
          prevHeader.minSize !== nextHeader.minSize ||
          prevHeader.offset !== nextHeader.offset ||
          prevHeader.resizable !== nextHeader.resizable ||
          prevHeader.sortable !== nextHeader.sortable ||
          prevHeader.sortDirection !== nextHeader.sortDirection ||
          prevHeader.stickyPosition !== nextHeader.stickyPosition ||
          prevHeader.width !== nextHeader.width;

        if (hasDiff) {
          mHeaderById.current[header.id] = nextHeader;
        }
      }

      const headers = visibleHeaders.map(header => mHeaderById.current[header.id]);
      const mHeaderGroup = mHeaderGroupById.current[headerGroup.id];
      const hasDiff = !mHeaderGroup || !compareArrayShallow(mHeaderGroup.headers, headers);

      if (hasDiff) {
        mHeaderGroupById.current[headerGroup.id] = {
          id: headerGroup.id,
          headers,
        };
      }

      comparingHeaderGroups.push(mHeaderGroupById.current[headerGroup.id]);
    }

    if (renderedCenterColumnIds) {
      for (const headerId of Object.keys(mHeaderById.current)) {
        if (!renderedHeaderIds.has(headerId)) {
          delete mHeaderById.current[headerId];
        }
      }
    }

    const hasDiff = !compareArrayShallow(memoizedHeaderGroups.current, comparingHeaderGroups);
    if (hasDiff) {
      memoizedHeaderGroups.current = comparingHeaderGroups;
    }

    result.headerGroups = memoizedHeaderGroups.current;
  }

  // headerCount
  {
    result.headerCount = isColumnVirtualizationEnabled
      ? table.getVisibleLeafColumns().length +
        (hasSelectionColumn ? 1 : 0) +
        (hasExpandColumn ? 1 : 0)
      : result.headerGroups.reduce(
          (count, headerGroup) =>
            Math.max(
              count,
              headerGroup.headers.length + (hasSelectionColumn ? 1 : 0) + (hasExpandColumn ? 1 : 0),
            ),
          0,
        );
  }

  // rows
  {
    const memoizedRows = useRef<DataTableRowContent<TData>[]>([]);
    const comparingRows: DataTableRowContent<TData>[] = [];
    const mRowById = useRef<Record<string, DataTableRowContent<TData>>>({});
    const mCellById = useRef<Record<string, DataTableCellContent<TData>>>({});
    const renderedCellIds = new Set<string>();

    const rows = table.getRowModel().rows;
    for (const row of rows) {
      const rowVisibleCells = row.getVisibleCells();
      const visibleCells = renderedCenterColumnIds
        ? rowVisibleCells.filter(
            cell =>
              cell.column.getIsPinned() !== false || renderedCenterColumnIds.has(cell.column.id),
          )
        : rowVisibleCells;

      for (const cell of visibleCells) {
        renderedCellIds.add(cell.id);
        const prev = mCellById.current[cell.id];
        const next: DataTableCellContent<TData> = {
          id: cell.id,
          isResizing: cell.column.getIsResizing(),
          CellTemplate: cell.column.columnDef.cell,
          cellTemplateProps: cell.getContext(),
          meta: cell.column.columnDef.meta,
          offset: `var(${createColumnVariableKeys(cell.column.id).offset})`,
          stickyPosition: cell.column.getIsPinned(),
        };

        const hasDiff =
          !prev ||
          prev.isResizing !== next.isResizing ||
          prev.CellTemplate !== next.CellTemplate ||
          prev.cellTemplateProps !== next.cellTemplateProps ||
          prev.meta !== next.meta ||
          prev.offset !== next.offset ||
          prev.stickyPosition !== next.stickyPosition;

        if (hasDiff) {
          mCellById.current[cell.id] = next;
        }
      }

      const prev = mRowById.current[row.id];
      const expandingHidden = row.getIsExpandingHidden?.() ?? false;
      const rowInteractionDisabled = !(row.getCanInteract?.() ?? true);
      const selectionHidden = row.getIsSelectionHidden?.() ?? false;
      const next: DataTableRowContent<TData> = {
        id: row.id,
        cells: visibleCells.map(cell => mCellById.current[cell.id]),
        depth: row.depth,
        selected: row.getIsSelected(),
        selectionDisabled: !selectionHidden && !row.getCanSelect(),
        selectionHidden,
        expanded: row.getIsExpanded(),
        canExpand: row.getCanExpand(),
        expandingHidden,
        rowInteractionDisabled,
      };

      const hasDiff =
        !prev ||
        prev.selected !== next.selected ||
        prev.selectionDisabled !== next.selectionDisabled ||
        prev.selectionHidden !== next.selectionHidden ||
        prev.expanded !== next.expanded ||
        prev.canExpand !== next.canExpand ||
        prev.expandingHidden !== next.expandingHidden ||
        prev.rowInteractionDisabled !== next.rowInteractionDisabled ||
        prev.depth !== next.depth ||
        !compareArrayShallow(prev.cells, next.cells);

      if (hasDiff) {
        mRowById.current[row.id] = next;
      }

      comparingRows.push(mRowById.current[row.id]);
    }

    if (renderedCenterColumnIds) {
      for (const cellId of Object.keys(mCellById.current)) {
        if (!renderedCellIds.has(cellId)) {
          delete mCellById.current[cellId];
        }
      }
    }

    const hasDiff = !compareArrayShallow(memoizedRows.current, comparingRows);
    if (hasDiff) {
      memoizedRows.current = comparingRows;
    }

    result.rows = memoizedRows.current;
  }

  return result;
}

function compareArrayShallow<T>(source: T[], target: T[]): boolean {
  return source.length === target.length && source.every((value, index) => value === target[index]);
}
